import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Icon, IconButton } from 'components';
import { ButtonV3, DropdownV3, InputV2 } from 'components/ComponentV2';
import { InputDropdownCombo } from 'components/ComponentV2/InputDropdownCombo/InputDropdownCombo.component';
import { DatePicker } from 'components/DatePickerV3/DatePicker.component';
import Modal from 'components/Modal';
import Tooltip from 'components/Tooltip';
import { Checkbox, TextArea } from 'components/form';
import { Row } from 'components/layout';

import {
  SchoolDetailsType,
  UniversityDetailsType,
} from 'hooks/useCandidateProfile';
import useScrollOffset from 'hooks/useScrollOffset';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import { useCommonList } from 'lib/contexts/common-list-context';
import { useAuth } from 'lib/providers/AuthProvider';
import { formatDateToSave, replaceLineBreaksWithBullet } from 'utils/format';
import DeleteModal from '../DeleteModal';
import styles from '../ProfileModal.common.module.scss';
import {
  EDUCATION_TYPE,
  getUpdateSchoolValue,
  getUpdateUniversityValue,
} from './utils';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  universityInfo: UniversityDetailsType | null;
  schoolInfo: SchoolDetailsType | null;
  isEdit: boolean;
}

interface TabType {
  id: string;
  label: string;
}

export const AddEducationModal = memo(
  ({
    isOpen,
    onClose,
    universityInfo,
    schoolInfo,
    isEdit,
  }: ProfileModalProps) => {
    const { universities, subjects, degrees, studyYearOptions } =
      useCommonList();

    const [t] = useTranslation('candidate.profile.modal');

    const [candidateUniversityDetails, setCandidateUniversityDetails] =
      useState<UniversityDetailsType>({
        id: uuidv4(),
        type: EDUCATION_TYPE.University,
        university: {
          id: '',
          name: '',
        },
        degree: {
          id: '',
          name: '',
        },
        subject: {
          id: '',
          name: '',
        },
        study_year: '',
        start_date: '',
        end_date: '',
        currently_studying: false,
        description: '',
        isNew: true,
        degree_name: '',
        location: '',
      });
    const [showDegreeName, setShowDegreeName] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDateError, setDateError] = useState<boolean | null>(false);
    const [candidateSchoolDetails, setCandidateSchoolDetails] =
      useState<SchoolDetailsType>({
        id: uuidv4(),
        type: EDUCATION_TYPE.School,
        school_name: '',
        school_type: '',
        start_date: '',
        end_date: '',
        description: '',
        location: '',
        isNew: true,
      });
    const { updateCandidate } = useCandidateProfile();
    const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
      null
    );
    const { candidate } = useAuth();
    const { graduation_year } = candidate || {};
    const contentRef = useCallback((element: HTMLDivElement) => {
      if (element) {
        setContentElement(element);
      }
    }, []);

    const startDateDropdownRef = useRef<HTMLDivElement>(null);
    const endDateDropdownRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const degreeDropdownRef = useRef<HTMLButtonElement>(null);
    const subjectDropdownRef = useRef<HTMLButtonElement>(null);
    const universityDropdownRef = useRef<HTMLInputElement>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { getScrollOffset } = useScrollOffset();
    const tabsList: TabType[] = [
      {
        id: EDUCATION_TYPE.University,
        label: t('education.tab-list-label.university'),
      },
      {
        id: EDUCATION_TYPE.School,
        label: t('education.tab-list-label.school'),
      },
    ];

    const [selectedTab, setSelectedTab] = useState<TabType>(tabsList[0]);

    const onTabChange = (tab: TabType) => () => {
      setSelectedTab(tab);
      setIsSubmitted(false);
    };

    const scrollToDropdown = (dropdownRef: React.RefObject<HTMLElement>) => {
      const event = new Event('scroll');
      if (contentElement) {
        const topSpace = 12;
        const adjustment =
          contentElement.getBoundingClientRect().top + topSpace;
        const offset = getScrollOffset(
          dropdownRef,
          { current: contentElement },
          headerRef,
          adjustment
        );
        contentElement.scrollTop = offset;
        contentElement.dispatchEvent(event);
      }
    };

    const scrollToDropdownStartDate = () => {
      scrollToDropdown(startDateDropdownRef);
    };

    const scrollToDropdownEndDate = () => {
      scrollToDropdown(endDateDropdownRef);
    };

    const scrollToDropdownDegree = () => {
      scrollToDropdown(degreeDropdownRef);
    };

    const scrollToDropdownSubject = () => {
      scrollToDropdown(subjectDropdownRef);
    };

    const scrollToDropdownUniversity = () => {
      scrollToDropdown(universityDropdownRef);
    };

    useEffect(() => {
      const event = new Event('scroll-top');

      const handleScroll = () => {
        window.dispatchEvent(event);
      };

      if (contentElement) {
        contentElement.addEventListener('scroll', handleScroll);
      }

      return () => {
        if (contentElement) {
          contentElement.removeEventListener('scroll', handleScroll);
        }
      };
    }, [contentElement]);

    useEffect(() => {
      if (isEdit) {
        if (universityInfo) {
          const { type } = universityInfo;
          if (type === EDUCATION_TYPE.University) {
            setSelectedTab(tabsList[0]);
          }
          setCandidateUniversityDetails({ ...universityInfo, isNew: false });
          if (universityInfo.degree_name) {
            setShowDegreeName(true);
          }
        } else if (schoolInfo) {
          const { type } = schoolInfo;
          if (type === EDUCATION_TYPE.School) {
            setSelectedTab(tabsList[1]);
          }
          setCandidateSchoolDetails({ ...schoolInfo, isNew: false });
        }
      }
    }, [universityInfo, schoolInfo, isEdit]);

    const onUniversitySelect = (value: string) => {
      const university = universities.find(
        (university) => university.label.toLowerCase() === value.toLowerCase()
      );
      if (university) {
        setCandidateUniversityDetails((info) => ({
          ...info,
          university: {
            id: university?.id,
            name: university?.label,
          },
        }));
      } else {
        setCandidateUniversityDetails((info) => ({
          ...info,
          university: {
            id: '',
            name: value,
          },
        }));
      }
    };

    const onDegreeSelect = (value: string) => {
      const degree = degrees.find((degree) => degree.id === value);
      if (degree) {
        setCandidateUniversityDetails((info) => ({
          ...info,
          degree: {
            id: degree?.id,
            name: degree?.label,
          },
        }));
      }
    };

    const onSubjectSelect = (value: string) => {
      const subject = subjects.find((degree) => degree.id === value);
      if (subject) {
        setCandidateUniversityDetails((info) => ({
          ...info,
          subject: {
            id: subject?.id,
            name: subject?.label,
          },
        }));
      }
    };

    const onSubmit = async () => {
      if (isInvalid) return;
      setIsSaving(true);
      if (selectedTab.id === EDUCATION_TYPE.University) {
        const education = await getUpdateUniversityValue(
          candidateUniversityDetails
        );
        await updateCandidate({ ...education });
      } else if (selectedTab.id === EDUCATION_TYPE.School) {
        const { education_histories_attributes } = await getUpdateSchoolValue(
          candidateSchoolDetails
        );
        await updateCandidate({ education_histories_attributes });
      }
      setIsSaving(false);
      onClose();
    };

    const isInvalid = useMemo(() => {
      if (selectedTab.id === EDUCATION_TYPE.University) {
        if (candidateUniversityDetails.currently_studying) {
          if (!candidateUniversityDetails.study_year) return true;
        }
        if (
          !candidateUniversityDetails.degree.id ||
          !(
            candidateUniversityDetails.subject.name ||
            candidateUniversityDetails.degree_name
          ) ||
          !candidateUniversityDetails.start_date ||
          !candidateUniversityDetails.end_date ||
          !candidateUniversityDetails.description ||
          !candidateUniversityDetails.university.name ||
          !candidateUniversityDetails.location
        ) {
          return true;
        } else if (isDateError) return true;
      }
      if (selectedTab.id === EDUCATION_TYPE.School) {
        const {
          school_name,
          school_type,
          start_date,
          end_date,
          description,
          location,
        } = candidateSchoolDetails;
        if (
          !school_name ||
          !school_type ||
          !start_date ||
          !end_date ||
          !description ||
          !location
        )
          return true;
      } else if (isDateError) return true;

      return false;
    }, [
      candidateUniversityDetails,
      candidateSchoolDetails,
      selectedTab,
      isDateError,
    ]);

    const getTextAreaUniversity = useMemo(() => {
      return (
        <TextArea
          isDynamic={true}
          maxLength={600}
          label={t('education.description.label')}
          placeholder={t('education.description.university-placeholder')}
          value={candidateUniversityDetails?.description}
          onChange={(event) => {
            if (
              (candidateUniversityDetails?.description as string).length ===
              event.target.value.length + 1
            ) {
              setCandidateUniversityDetails((info) => ({
                ...info,
                description: event.target.value,
              }));
            } else if (event.target.value.endsWith('\n')) {
              setCandidateUniversityDetails((info) => ({
                ...info,
                description: event.target.value + '• ',
              }));
            } else {
              const description =
                candidateUniversityDetails?.description?.length === 0
                  ? '• ' + event.target.value
                  : event.target.value;
              setCandidateUniversityDetails((info) => ({
                ...info,
                description,
              }));
            }
          }}
          onFocus={() => {
            if (candidateUniversityDetails?.description?.length === 0) {
              setCandidateUniversityDetails((info) => ({
                ...info,
                description: '• ',
              }));
            }
          }}
          onPaste={(event: React.ClipboardEvent<HTMLTextAreaElement>) => {
            event.preventDefault();
            const clipboardData = event.clipboardData;
            const clipboardValue = clipboardData.getData('Text');

            // Replace line breaks with bullet points
            let value = replaceLineBreaksWithBullet(clipboardValue);

            // Get the selection range
            const selectionStart = event.currentTarget.selectionStart;
            const selectionEnd = event.currentTarget.selectionEnd;

            // Add a bullet at the beginning if one doesn't already exist
            if (
              !value.startsWith('• ') &&
              selectionStart !== null &&
              candidateUniversityDetails?.description?.substring(
                selectionStart - 2,
                selectionStart
              ) != '• '
            ) {
              if (
                candidateUniversityDetails?.description?.substring(
                  selectionStart - 1,
                  selectionStart
                ) == '\n' ||
                selectionStart == 0
              ) {
                value = '• ' + value;
              }
            }

            if (selectionStart !== null && selectionEnd !== null) {
              // Get the text before and after the selected range
              const textBeforeSelection =
                candidateUniversityDetails?.description?.substring(
                  0,
                  selectionStart
                ) || '';
              const textAfterSelection =
                candidateUniversityDetails?.description?.substring(
                  selectionEnd
                ) || '';

              // Update details with the replaced text
              setCandidateUniversityDetails((info) => ({
                ...info,
                description: textBeforeSelection + value + textAfterSelection,
              }));
            }
          }}
          isValid={!(isSubmitted && !candidateUniversityDetails.description)}
          className={cn(styles.textarea, styles.gap)}
          infoToolTip={
            <Tooltip
              content={t('education.description.tooltip')}
              position="top"
              color="dark"
              className={cn(styles.tooltipContainer, styles.translate25p)}
            >
              <Icon
                iconName="icon_info"
                className={styles.tooltipContainerIcon}
                size="auto"
              />
            </Tooltip>
          }
        />
      );
    }, [candidateUniversityDetails, isSubmitted]);

    const renderUniversityForm = () => {
      const startDate = candidateUniversityDetails?.start_date
        ? new Date(candidateUniversityDetails.start_date)
        : null;
      const endDate = candidateUniversityDetails.end_date
        ? new Date(candidateUniversityDetails.end_date)
        : null;

      return (
        <>
          <InputDropdownCombo
            label={t('education.university.label')}
            placeholder={t('education.university.placeholder')}
            options={universities}
            value={candidateUniversityDetails?.university?.name || ''}
            onChange={(value) => onUniversitySelect(value)}
            isValid={
              !(isSubmitted && !candidateUniversityDetails?.university?.name)
            }
            scrollToDropdown={scrollToDropdownUniversity}
            ref={universityDropdownRef}
            className={styles.gap}
          />
          <DropdownV3
            label={t('education.degree.label')}
            title={t('education.degree.title')}
            value={candidateUniversityDetails?.degree?.id || ''}
            onChange={(value) => onDegreeSelect(value)}
            options={degrees}
            isSearchable={true}
            searchPlaceholder="Search"
            selectedValueLabel={candidateUniversityDetails?.degree?.name || ''}
            scrollToDropdown={scrollToDropdownDegree}
            isValid={!(isSubmitted && !candidateUniversityDetails?.degree?.id)}
            ref={degreeDropdownRef}
            className={styles.gap}
          />
          <DropdownV3
            label={t('education.subject-area.label')}
            title={t('education.subject-area.title')}
            value={candidateUniversityDetails?.subject?.id || ''}
            onChange={(value) => onSubjectSelect(value)}
            options={subjects}
            isSearchable={true}
            searchPlaceholder="Search"
            selectedValueLabel={candidateUniversityDetails?.subject?.name || ''}
            isValid={
              !(
                isSubmitted &&
                !(
                  candidateUniversityDetails?.subject?.id ||
                  candidateUniversityDetails?.degree_name
                )
              )
            }
            className={cn(styles.gap, styles['subject-dropdown'])}
            scrollToDropdown={scrollToDropdownSubject}
            ref={subjectDropdownRef}
          />
          {!showDegreeName && (
            <Tooltip
              content={t('education.specific-degree.tooltip')}
              position="top"
              color="dark"
              className={cn(styles.tooltipContainer, styles.translate25p)}
              containerClassName={styles.tooltipContainerContent}
            >
              <div
                onClick={() => setShowDegreeName(true)}
                className={cn(styles.gap, styles.linkText)}
              >
                {t('education.specific-degree.title')}
              </div>
            </Tooltip>
          )}
          {showDegreeName && (
            <InputV2
              label={t('education.specific-degree.label')}
              placeholder={t('education.specific-degree.placeholder')}
              showOptionalLabel={false}
              value={candidateUniversityDetails?.degree_name}
              onChange={(event) => {
                setCandidateUniversityDetails((info) => ({
                  ...info,
                  degree_name: event.target.value,
                }));
              }}
              isInvalid={
                isSubmitted &&
                !(
                  candidateUniversityDetails?.subject?.id ||
                  candidateUniversityDetails?.degree_name
                )
              }
              infoToolTip={
                <Tooltip
                  content={t('education.specific-degree.tooltip')}
                  position="top"
                  xAlign="center"
                  color="dark"
                  className={cn(styles.tooltipContainer, styles.translate25p)}
                  containerClassName={styles.tooltipContainerContent}
                >
                  <Icon
                    iconName="icon_info"
                    className={styles.tooltipContainerIcon}
                    size="auto"
                  />
                </Tooltip>
              }
              className={styles.gap}
            />
          )}
          <Checkbox
            checked={candidateUniversityDetails?.currently_studying}
            onChange={() => {
              setCandidateUniversityDetails((info) => ({
                ...info,
                currently_studying:
                  !candidateUniversityDetails?.currently_studying,
                end_date:
                  (!candidateUniversityDetails?.currently_studying &&
                    new Date(candidateUniversityDetails.end_date || '') <
                      new Date()) ||
                  candidateUniversityDetails?.currently_studying
                    ? ''
                    : candidateUniversityDetails.end_date,
                study_year: candidateUniversityDetails.currently_studying
                  ? null
                  : candidateUniversityDetails.study_year,
              }));
            }}
            className={cn(styles.ProfileModalCheckbox, styles.gap)}
          >
            {t('education.currently-studying')}
          </Checkbox>
          <Row justify="space-between" className={styles.dateContainer}>
            <DatePicker
              label={t('common.start-date.label')}
              placeholder={t('common.start-date.placeholder')}
              value={startDate || null}
              onChange={(value) =>
                setCandidateUniversityDetails((info) => ({
                  ...info,
                  start_date: formatDateToSave(value),
                }))
              }
              className={cn(styles.date, styles.gap, {
                [styles.errorGap]: isDateError,
              })}
              dateSelectorClassName={cn(styles.left, styles.bottom)}
              dateInputClassName={styles.textarea}
              hasError={
                (isSubmitted && !candidateUniversityDetails.start_date) ||
                isDateError
              }
              ref={startDateDropdownRef}
              scrollToDropdown={scrollToDropdownStartDate}
            />
            <DatePicker
              label={
                candidateUniversityDetails?.currently_studying
                  ? t('education.expected-graduation-date')
                  : t('education.graduation-date')
              }
              placeholder={t('education.end-date.placeholder')}
              value={
                endDate ? endDate : new Date(graduation_year?.toString() || '')
              }
              showOnlyYear={!endDate}
              onChange={(value) =>
                setCandidateUniversityDetails((info) => ({
                  ...info,
                  end_date: formatDateToSave(value),
                }))
              }
              minDate={
                candidateUniversityDetails?.currently_studying
                  ? new Date()
                  : startDate || undefined
              }
              className={cn(styles.date, styles.gap, {
                [styles.errorGap]: isDateError,
              })}
              dateSelectorClassName={styles.bottom}
              dateInputClassName={styles.textarea}
              hasError={
                (isSubmitted && !candidateUniversityDetails.end_date) ||
                isDateError
              }
              ref={endDateDropdownRef}
              scrollToDropdown={scrollToDropdownEndDate}
              isFutureDate={
                candidateUniversityDetails?.currently_studying ? true : false
              }
            />
          </Row>
          {isDateError ? (
            <>
              <div className={styles.errorMessage}>
                <Icon
                  iconName="icon_info"
                  className={styles.errorMessageIcon}
                  size="auto"
                />
                <p>{`${
                  candidateUniversityDetails?.currently_studying
                    ? t('education.expected-graduation-date')
                    : t('education.graduation-date')
                } ${t('education.end-date.error')}`}</p>
              </div>
              <div className={styles.gap}></div>
            </>
          ) : null}
          {candidateUniversityDetails?.currently_studying && (
            <DropdownV3
              title={t('education.study-year.title')}
              label={t('education.study-year.label')}
              value={candidateUniversityDetails?.study_year || ''}
              selectedValueLabel={candidateUniversityDetails?.study_year || ''}
              onChange={(value) =>
                setCandidateUniversityDetails((info) => ({
                  ...info,
                  study_year: value,
                }))
              }
              options={studyYearOptions}
              className={styles.gap}
              isValid={!(isSubmitted && !candidateUniversityDetails.study_year)}
            />
          )}

          {getTextAreaUniversity}
          <InputV2
            label={t('common.location.label')}
            placeholder={t('common.location.placeholder')}
            maxLength={160}
            value={candidateUniversityDetails?.location}
            onChange={(event) =>
              setCandidateUniversityDetails((info) => ({
                ...info,
                location: event.target.value,
              }))
            }
            className={styles.gap}
            isInvalid={isSubmitted && !candidateUniversityDetails.location}
          />
        </>
      );
    };

    const getTextAreaSchool = useMemo(() => {
      return (
        <TextArea
          maxLength={600}
          label={t('education.description.label')}
          placeholder={t('education.description.school-placeholder')}
          value={candidateSchoolDetails?.description}
          onChange={(event) => {
            if (
              (candidateSchoolDetails?.description as string).length ===
              event.target.value.length + 1
            ) {
              setCandidateSchoolDetails((info) => ({
                ...info,
                description: event.target.value,
              }));
            } else if (event.target.value.endsWith('\n')) {
              setCandidateSchoolDetails((info) => ({
                ...info,
                description: event.target.value + '• ',
              }));
            } else {
              const description =
                candidateSchoolDetails?.description?.length === 0
                  ? '• ' + event.target.value
                  : event.target.value;
              setCandidateSchoolDetails((info) => ({
                ...info,
                description,
              }));
            }
          }}
          onFocus={() => {
            if (candidateSchoolDetails?.description?.length === 0) {
              setCandidateSchoolDetails((info) => ({
                ...info,
                description: '• ',
              }));
            }
          }}
          onPaste={(event: React.ClipboardEvent<HTMLTextAreaElement>) => {
            event.preventDefault();
            const clipboardData = event.clipboardData;
            const clipboardValue = clipboardData.getData('Text');

            // Replace line breaks with bullet points
            let value = replaceLineBreaksWithBullet(clipboardValue);

            // Get the selection range
            const selectionStart = event.currentTarget.selectionStart;
            const selectionEnd = event.currentTarget.selectionEnd;

            // Add a bullet at the beginning if one doesn't already exist
            if (
              !value.startsWith('• ') &&
              selectionStart !== null &&
              candidateSchoolDetails?.description?.substring(
                selectionStart - 2,
                selectionStart
              ) != '• '
            ) {
              if (
                candidateSchoolDetails?.description?.substring(
                  selectionStart - 1,
                  selectionStart
                ) == '\n' ||
                selectionStart == 0
              ) {
                value = '• ' + value;
              }
            }

            if (selectionStart !== null && selectionEnd !== null) {
              // Get the text before and after the selected range
              const textBeforeSelection =
                candidateSchoolDetails?.description?.substring(
                  0,
                  selectionStart
                ) || '';
              const textAfterSelection =
                candidateSchoolDetails?.description?.substring(selectionEnd) ||
                '';

              // Update details with the replaced text
              setCandidateSchoolDetails((info) => ({
                ...info,
                description: textBeforeSelection + value + textAfterSelection,
              }));
            }
          }}
          isValid={!(isSubmitted && !candidateSchoolDetails.description)}
          className={styles.gap}
          infoToolTip={
            <Tooltip
              content={t('education.description.tooltip')}
              position="top"
              color="dark"
              className={cn(styles.tooltipContainer, styles.translate25p)}
            >
              <Icon
                iconName="icon_info"
                className={styles.tooltipContainerIcon}
                size="auto"
              />
            </Tooltip>
          }
        />
      );
    }, [candidateSchoolDetails, isSubmitted]);

    const renderSchoolForm = () => {
      const startDate = candidateSchoolDetails?.start_date
        ? new Date(candidateSchoolDetails.start_date)
        : null;
      const endDate = candidateSchoolDetails.end_date
        ? new Date(candidateSchoolDetails.end_date)
        : null;

      return (
        <>
          <InputV2
            label={t('education.school-type.label')}
            placeholder={t('education.school-type.placeholder')}
            maxLength={160}
            value={candidateSchoolDetails?.school_type}
            onChange={(event) =>
              setCandidateSchoolDetails((info) => ({
                ...info,
                school_type: event.target.value,
              }))
            }
            className={styles.gap}
            isInvalid={isSubmitted && !candidateSchoolDetails.school_type}
          />
          <InputV2
            label={t('education.school-name.label')}
            placeholder={t('education.school-name.placeholder')}
            maxLength={160}
            value={candidateSchoolDetails?.school_name}
            onChange={(event) =>
              setCandidateSchoolDetails((info) => ({
                ...info,
                school_name: event.target.value,
              }))
            }
            className={styles.gap}
            isInvalid={isSubmitted && !candidateSchoolDetails.school_name}
          />
          <Row justify="space-between" className={styles.dateContainer}>
            <DatePicker
              label={t('common.start-date.label')}
              placeholder={t('common.start-date.placeholder')}
              value={startDate || null}
              onChange={(value) =>
                setCandidateSchoolDetails((info) => ({
                  ...info,
                  start_date: formatDateToSave(value),
                }))
              }
              className={cn(styles.date, styles.gap, {
                [styles.errorGap]: isDateError,
              })}
              dateSelectorClassName={cn(styles.left, {
                [styles.bottom]: !isEdit,
              })}
              dateInputClassName={styles.textarea}
              hasError={
                (isSubmitted && !candidateSchoolDetails.start_date) ||
                isDateError
              }
              ref={startDateDropdownRef}
              scrollToDropdown={scrollToDropdownStartDate}
            />

            <DatePicker
              label={t('common.end-date.label')}
              placeholder={t('common.end-date.placeholder')}
              value={endDate || null}
              onChange={(value) =>
                setCandidateSchoolDetails((info) => ({
                  ...info,
                  end_date: formatDateToSave(value),
                }))
              }
              className={cn(styles.date, styles.gap, {
                [styles.errorGap]: isDateError,
              })}
              minDate={startDate || undefined}
              dateSelectorClassName={cn({ [styles.bottom]: !isEdit })}
              dateInputClassName={styles.textarea}
              hasError={
                (isSubmitted && !candidateSchoolDetails.end_date) || isDateError
              }
              ref={endDateDropdownRef}
              scrollToDropdown={scrollToDropdownEndDate}
            />
          </Row>
          {isDateError ? (
            <>
              <div className={styles.errorMessage}>
                <Icon
                  iconName="icon_info"
                  className={styles.errorMessageIcon}
                  size="auto"
                />
                <p>{t('common.end-date.error')}</p>
              </div>
              <div className={styles.gap}></div>
            </>
          ) : null}
          {getTextAreaSchool}
          <InputV2
            label={t('common.location.label')}
            placeholder={t('common.location.placeholder')}
            maxLength={160}
            value={candidateSchoolDetails?.location}
            onChange={(event) =>
              setCandidateSchoolDetails((info) => ({
                ...info,
                location: event.target.value,
              }))
            }
            className={styles.gap}
            isInvalid={isSubmitted && !candidateSchoolDetails.location}
          />
        </>
      );
    };

    useEffect(() => {
      let startDate;
      let endDate;
      if (selectedTab.id === EDUCATION_TYPE.University) {
        startDate = candidateUniversityDetails.start_date
          ? new Date(candidateUniversityDetails.start_date)
          : null;
        endDate = candidateUniversityDetails.end_date
          ? new Date(candidateUniversityDetails.end_date)
          : null;
      } else if (selectedTab.id === EDUCATION_TYPE.School) {
        startDate = candidateSchoolDetails.start_date
          ? new Date(candidateSchoolDetails.start_date)
          : null;
        endDate = candidateSchoolDetails.end_date
          ? new Date(candidateSchoolDetails.end_date)
          : null;
      }
      if (startDate && endDate && endDate < startDate) {
        setDateError(true);
      } else {
        setDateError(false);
      }
    }, [
      selectedTab.id,
      candidateUniversityDetails.start_date,
      candidateUniversityDetails.end_date,
      candidateSchoolDetails.start_date,
      candidateSchoolDetails.end_date,
    ]);

    const deleteEducation = async () => {
      setIsDeleting(true);
      let id = '';
      if (selectedTab.id === EDUCATION_TYPE.University) {
        id = candidateUniversityDetails.id;
      } else if (selectedTab.id === EDUCATION_TYPE.School) {
        id = candidateSchoolDetails.id;
      }
      await updateCandidate(
        {
          education_histories_attributes: {
            id,
            _destroy: true,
          },
        },
        false,
        true
      );

      setIsDeleting(false);
      onClose();
    };

    return (
      <>
        <Modal
          isOpen={isOpen}
          onRequestClose={onClose}
          contentClassName={cn(styles.ProfileModal)}
        >
          <div className={styles.ProfileModalHeader} ref={headerRef}>
            {isEdit ? (
              <p>{t('education.edit')}</p>
            ) : (
              <p>{t('education.add')}</p>
            )}{' '}
            <IconButton
              onClick={onClose}
              className={styles.ProfileModalHeaderClose}
              iconName="close"
              size="small"
            />
            <IconButton
              onClick={onClose}
              className={styles.ProfileModalHeaderCloseMobile}
              iconName="arrow-button-left"
              size="small"
            />
          </div>
          <div ref={contentRef} className={styles.ProfileModalContent}>
            {isEdit ? null : (
              <div className={styles.ProfileModalTabs}>
                {tabsList.map((tab) => (
                  <div
                    key={tab.id}
                    className={cn(styles.tab, {
                      [styles.selected]: tab.id === selectedTab.id,
                    })}
                    onClick={onTabChange(tab)}
                  >
                    <p>{tab.label}</p>
                  </div>
                ))}
              </div>
            )}
            {selectedTab.id === EDUCATION_TYPE.University &&
              renderUniversityForm()}
            {selectedTab.id === EDUCATION_TYPE.School && renderSchoolForm()}
          </div>
          <div
            className={cn(styles.ProfileModalAction, {
              [styles.singleCta]: !isEdit,
            })}
          >
            {isEdit && (
              <ButtonV3
                variant="text"
                color="secondary"
                onClick={() => setIsDeleteModalOpen(true)}
                className={styles.button}
                disabled={isDeleting}
                isLoading={isDeleting}
              >
                {t('common.delete')}
              </ButtonV3>
            )}
            <ButtonV3
              onClick={() => {
                setIsSubmitted(true);
                onSubmit();
              }}
              className={styles.button}
              disabled={isSaving}
              isLoading={isSaving}
            >
              {isEdit ? t('common.done') : t('education.add')}
            </ButtonV3>
          </div>
        </Modal>
        {isDeleteModalOpen && (
          <DeleteModal
            isOpen={isDeleteModalOpen}
            onCancel={() => setIsDeleteModalOpen(false)}
            onConfirm={deleteEducation}
            isButtonLoading={isDeleting}
            type={t('education.title')}
          />
        )}
      </>
    );
  }
);

AddEducationModal.displayName = 'AddEducationModal';
